import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { Clock, Users, ChevronLeft, ChevronRight, Check, X, Filter } from "lucide-react";
import { useState, useMemo } from "react";
import { format, addDays, startOfWeek, isSameDay, isBefore } from "date-fns";
import { de } from "date-fns/locale";
import { GymHeader } from "@/components/GymHeader";
import { toast } from "sonner";
import { Link } from "wouter";
import { trackGymSlotBooked } from "@/lib/tracking";

const DAYS_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const WEEKDAYS = [1, 2, 3, 4, 5]; // Monday to Friday only

// Gym-specific colors (blue/cyan theme to distinguish from course colors)
const GYM_COLORS = {
  available: "bg-cyan-50 border-cyan-300 text-cyan-900",
  booked: "bg-blue-100 border-blue-400 text-blue-900",
  full: "bg-slate-100 border-slate-300 text-slate-600",
  past: "bg-slate-50 border-slate-200 text-slate-400",
};

export default function GymCalendar() {
  const { user, loading: authLoading } = useAuth();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedService, setSelectedService] = useState<string>("all");
  const [bookingSlot, setBookingSlot] = useState<number | null>(null);
  
  const utils = trpc.useUtils();

  const { data: config } = trpc.gym.getConfig.useQuery();
  const { data: services } = trpc.gym.services.list.useQuery({});
  
  const weekEnd = addDays(currentWeekStart, 7);
  const { data: slots, isLoading: slotsLoading } = trpc.gym.slots.list.useQuery({
    startDate: currentWeekStart.toISOString(),
    endDate: weekEnd.toISOString(),
    serviceId: selectedService !== "all" ? parseInt(selectedService) : undefined
  });

  const { data: myBookings } = trpc.gym.member.myBookings.useQuery(
    { status: "confirmed" },
    { enabled: !!user }
  );

  const bookMutation = trpc.gym.member.book.useMutation({
    onSuccess: () => {
      // Track gym slot booking
      if (selectedSlot) {
        trackGymSlotBooked(selectedSlot.id, selectedSlot.service?.name || 'Unknown');
      }
      toast.success("Slot erfolgreich gebucht!");
      setBookingSlot(null);
      utils.gym.slots.list.invalidate();
      utils.gym.member.myBookings.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const isGymMember = user?.roles?.includes("gym_member");
  const gymStatus = (user as any)?.gymStatus;
  const canBook = isGymMember && gymStatus === "approved";

  // Group slots by day
  const slotsByDay = useMemo(() => {
    if (!slots) return {};
    const grouped: Record<number, typeof slots> = {};
    
    WEEKDAYS.forEach(dayOfWeek => {
      grouped[dayOfWeek] = slots.filter(slot => {
        const slotDate = new Date(slot.startTime);
        return slotDate.getDay() === dayOfWeek;
      }).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    });
    
    return grouped;
  }, [slots]);

  const selectedSlot = slots?.find(s => s.id === bookingSlot);
  const isSlotBooked = (slotId: number) => 
    myBookings?.some(b => b.slot?.id === slotId);

  const getSlotStatus = (slot: NonNullable<typeof slots>[number]) => {
    const now = new Date();
    const slotStart = new Date(slot.startTime);
    
    // Check if booked first - show booked status even for past slots
    if (isSlotBooked(slot.id)) return "booked";
    if (isBefore(slotStart, now)) return "past";
    if (slot.bookingCount >= slot.maxParticipants) return "full";
    return "available";
  };

  const handleBook = () => {
    if (!bookingSlot) return;
    bookMutation.mutate({ slotId: bookingSlot });
  };

  const hasActiveFilters = selectedService !== "all";

  const clearFilters = () => {
    setSelectedService("all");
  };

  // Generate time slots for the grid (6:00 - 22:00)
  const timeSlots = Array.from({ length: 17 }, (_, i) => {
    const hour = 6 + i;
    return `${hour.toString().padStart(2, "0")}:00`;
  });

  return (
    <div className="min-h-screen bg-background">
      <GymHeader />
      
      <div className="container py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">
            Trainingskalender
          </h1>
          <p className="mt-2 text-muted-foreground">
            Übersicht aller verfügbaren Trainingsslots
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Filter className="h-4 w-4" />
                Filter:
              </div>
              
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Alle Bereiche" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Bereiche</SelectItem>
                  {services?.filter(s => s.isActive).map(service => (
                    <SelectItem key={service.id} value={service.id.toString()}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="h-4 w-4" />
                  Filter zurücksetzen
                </Button>
              )}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 border-t pt-4">
              <span className="text-sm text-muted-foreground">Legende:</span>
              <div className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded border ${GYM_COLORS.available}`} />
                <span className="text-sm">Verfügbar</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded border ${GYM_COLORS.booked}`} />
                <span className="text-sm">Von Ihnen gebucht</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded border ${GYM_COLORS.full}`} />
                <span className="text-sm">Ausgebucht</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded border ${GYM_COLORS.past}`} />
                <span className="text-sm">Vergangen</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Vorherige Woche
          </Button>
          
          <h2 className="text-lg font-semibold text-center sm:whitespace-nowrap">
            {format(currentWeekStart, "d.", { locale: de })} - {format(addDays(currentWeekStart, 4), "d. MMMM yyyy", { locale: de })}
          </h2>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}
          >
            Nächste Woche
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        {slotsLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Slots werden geladen...</p>
          </div>
        ) : (
          <>
            {/* Desktop Calendar Grid */}
            <div className="hidden lg:block overflow-x-auto">
              <div className="min-w-[900px]">
                {/* Header Row */}
                <div className="grid grid-cols-6 gap-1 mb-2">
                  <div className="p-2 text-center text-sm font-medium text-muted-foreground">
                    Zeit
                  </div>
                  {WEEKDAYS.map((day, idx) => {
                    const dayDate = addDays(currentWeekStart, idx);
                    return (
                      <div key={day} className="p-2 text-center">
                        <div className="font-semibold text-foreground">
                          {DAYS_DE[day]} {format(dayDate, "d.M.", { locale: de })}
                        </div>
                        <Badge variant="outline" className="mt-1">
                          {slotsByDay[day]?.length || 0}
                        </Badge>
                      </div>
                    );
                  })}
                </div>

                {/* Time Grid */}
                <div className="relative border rounded-lg overflow-hidden">
                  {/* Time slots background */}
                  {timeSlots.map((time, idx) => (
                    <div 
                      key={time} 
                      className={`grid grid-cols-6 gap-1 ${idx % 2 === 0 ? 'bg-muted/30' : 'bg-background'}`}
                      style={{ height: '60px' }}
                    >
                      <div className="p-2 text-xs text-muted-foreground border-r">
                        {time}
                      </div>
                      {WEEKDAYS.map(day => (
                        <div key={day} className="border-r last:border-r-0 relative" />
                      ))}
                    </div>
                  ))}

                  {/* Slot blocks overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="grid grid-cols-6 h-full">
                      <div /> {/* Time column spacer */}
                      {WEEKDAYS.map(day => {
                        const daySlots = slotsByDay[day] || [];
                        
                        // Calculate overlapping groups and column positions
                        const getTimeInMinutes = (time: string) => {
                          const date = new Date(time);
                          return date.getHours() * 60 + date.getMinutes();
                        };
                        
                        // Sort slots by start time
                        const sortedSlots = [...daySlots].sort((a, b) => 
                          getTimeInMinutes(a.startTime) - getTimeInMinutes(b.startTime)
                        );
                        
                        // Find overlapping slots and assign columns
                        const slotColumns: Map<number, { col: number; totalCols: number }> = new Map();
                        
                        sortedSlots.forEach((slot, idx) => {
                          const slotStart = getTimeInMinutes(slot.startTime);
                          const slotEnd = getTimeInMinutes(slot.endTime);
                          
                          // Find all slots that overlap with this one
                          const overlapping = sortedSlots.filter((other, otherIdx) => {
                            if (other.id === slot.id) return true;
                            const otherStart = getTimeInMinutes(other.startTime);
                            const otherEnd = getTimeInMinutes(other.endTime);
                            return slotStart < otherEnd && slotEnd > otherStart;
                          });
                          
                          // Assign column based on position in overlapping group
                          const myIndex = overlapping.findIndex(s => s.id === slot.id);
                          slotColumns.set(slot.id, {
                            col: myIndex,
                            totalCols: overlapping.length
                          });
                        });
                        
                        return (
                          <div key={day} className="relative">
                            {daySlots.map(slot => {
                              const startDate = new Date(slot.startTime);
                              const endDate = new Date(slot.endTime);
                              const startHour = startDate.getHours();
                              const startMin = startDate.getMinutes();
                              const endHour = endDate.getHours();
                              const endMin = endDate.getMinutes();
                              
                              const top = ((startHour - 6) * 60 + startMin);
                              const height = ((endHour - startHour) * 60 + (endMin - startMin));
                              
                              const colInfo = slotColumns.get(slot.id) || { col: 0, totalCols: 1 };
                              const widthPercent = 100 / colInfo.totalCols;
                              const leftPercent = colInfo.col * widthPercent;

                              const status = getSlotStatus(slot);
                              const statusColors = GYM_COLORS[status];
                              const available = slot.maxParticipants - slot.bookingCount;
                              
                              return (
                                <button
                                  key={slot.id}
                                  onClick={() => {
                                    if (status === "available" && canBook) {
                                      setBookingSlot(slot.id);
                                    } else if (status === "available" && !user) {
                                      window.location.href = getLoginUrl();
                                    }
                                  }}
                                  disabled={status !== "available" || !canBook}
                                  className={`absolute rounded border-l-4 p-1 pointer-events-auto transition-all flex flex-col ${statusColors} ${
                                    status === "available" && canBook ? "hover:shadow-md hover:z-10 cursor-pointer" : ""
                                  }`}
                                  style={{
                                    top: `${top}px`,
                                    height: `${Math.max(height - 2, 30)}px`,
                                    left: `calc(${leftPercent}% + 2px)`,
                                    width: `calc(${widthPercent}% - 4px)`,
                                    borderLeftColor: slot.service?.color ?? '#06b6d4' // cyan-500 as default
                                  }}
                                >
                                  <p className="text-xs font-medium truncate">{slot.service?.name}</p>
                                  <p className="text-xs opacity-75">
                                    {format(startDate, "HH:mm")} - {format(endDate, "HH:mm")}
                                  </p>
                                  <div className="flex items-center justify-between gap-1 mt-auto">
                                    <p className="text-xs opacity-75 flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {available}/{slot.maxParticipants}
                                    </p>
                                    {status === "booked" && (
                                      <Badge variant="default" className="text-xs bg-blue-600 inline-flex items-center whitespace-nowrap shrink-0">
                                        <Check className="h-3 w-3 mr-1" />
                                        Gebucht
                                      </Badge>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile/Tablet List View */}
            <div className="lg:hidden space-y-6">
              {WEEKDAYS.map(day => {
                const daySlots = slotsByDay[day] || [];
                return (
                  <div key={day}>
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cyan-700">
                      {DAYS_DE[day]}
                      <Badge variant="outline">{daySlots.length}</Badge>
                    </h3>
                    {daySlots.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg border-dashed">
                        Keine Slots
                      </p>
                    ) : (
                      <div className="flex flex-col gap-4">
                        {daySlots.map(slot => {
                          const status = getSlotStatus(slot);
                          const statusColors = GYM_COLORS[status];
                          const available = slot.maxParticipants - slot.bookingCount;
                          
                          return (
                            <button
                              key={slot.id}
                              onClick={() => {
                                if (status === "available" && canBook) {
                                  setBookingSlot(slot.id);
                                } else if (status === "available" && !user) {
                                  window.location.href = getLoginUrl();
                                }
                              }}
                              disabled={status !== "available" || !canBook}
                              className={`block w-full text-left`}
                            >
                              <Card className={`transition-all border-l-4 ${statusColors} ${
                                status === "available" && canBook ? "hover:shadow-md cursor-pointer" : ""
                              }`}
                                style={{
                                  borderLeftColor: slot.service?.color ?? '#06b6d4'
                                }}
                              >
                                <CardContent className="py-3">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium">{slot.service?.name}</p>
                                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          {format(new Date(slot.startTime), "HH:mm")} - {format(new Date(slot.endTime), "HH:mm")}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <Users className="h-3 w-3" />
                                          {available}/{slot.maxParticipants}
                                        </span>
                                      </div>
                                    </div>
                                    {status === "booked" && (
                                      <Badge variant="default" className="bg-blue-600">
                                        <Check className="h-3 w-3 mr-1" />
                                        Gebucht
                                      </Badge>
                                    )}
                                    {status === "full" && (
                                      <Badge variant="secondary">
                                        Ausgebucht
                                      </Badge>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Results count */}
            <div className="mt-6 text-center text-sm text-muted-foreground">
              {slots?.length || 0} {slots?.length === 1 ? 'Slot' : 'Slots'} gefunden
            </div>
          </>
        )}

        {/* Not a member notice */}
        {!canBook && user && (
          <Card className="mt-6 border-warning">
            <CardContent className="py-4">
              <p className="text-sm">
                {gymStatus === "pending" ? (
                  "Ihre Mitgliedsanfrage wird bearbeitet. Sie können Slots buchen, sobald Sie freigeschaltet wurden."
                ) : gymStatus === "rejected" ? (
                  "Ihre Mitgliedsanfrage wurde leider abgelehnt. Bitte kontaktieren Sie uns für weitere Informationen."
                ) : (
                  <>
                    Sie sind noch kein Gym-Mitglied.{" "}
                    <a href="/gym/register" className="text-primary hover:underline">
                      Jetzt Mitglied werden
                    </a>
                  </>
                )}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Booking Dialog */}
      <Dialog open={!!bookingSlot} onOpenChange={(open) => !open && setBookingSlot(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Slot buchen</DialogTitle>
            <DialogDescription>
              Möchten Sie diesen Trainingsslot buchen?
            </DialogDescription>
          </DialogHeader>
          
          {selectedSlot && (
            <div className="space-y-2 py-4">
              <div className="flex items-center gap-2">
                <span className="font-medium">Service:</span>
                <span>{selectedSlot.service?.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>
                  {format(new Date(selectedSlot.startTime), "EEEE, d. MMMM yyyy", { locale: de })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>
                  {format(new Date(selectedSlot.startTime), "HH:mm")} - {format(new Date(selectedSlot.endTime), "HH:mm")} Uhr
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>
                  {selectedSlot.maxParticipants - selectedSlot.bookingCount} von {selectedSlot.maxParticipants} Plätzen verfügbar
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingSlot(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleBook} disabled={bookMutation.isPending}>
              {bookMutation.isPending ? "Wird gebucht..." : "Jetzt buchen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
